import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider, initI18n } from '@wix/wix-i18n-config';
import { AppLoader } from './components/AppLoader';
import { PureExperimentsProvider as ExperimentsProvider } from '@wix/wix-experiments-react';
import Experiments from '@wix/wix-experiments';
import { AccountSettingsHttpApi } from './services/accountSettingsApi';
import { DealerApi } from './services/dealerApi';
import { Box, Loader } from '@wix/design-system';
import { detectPreset } from './utils/presetDetection';
import abTranslate from 'ab-translate/dist/src/abTranslate-runtime';
import { PanoramaService } from './services/panorama';
// Branded preset theme tokens, extracted into the render-blocking app.min.css so
// <html data-preset="..."> is themed on the first paint (server-agnostic — works
// for both the Scala/index.vm and Node/index.ejs render paths). Generated from
// presets.ts by scripts/generate-preset-css.cjs (gitignored, like messages_*.json).
import './common/styles/presetVars.global.css';

// Started before translations load and the first render, so a page that fails
// or stalls on the way to its first screen counts against the load success
// rate. RootStore.start() reports the finish.
const panorama = new PanoramaService(window.__STATICS_URL__);
panorama.reportLoadStart();

// Initialize experiments with data from window.__EXPERIMENTS__ if available
const experiments = new Experiments({
  scope: 'users',
  ...(window.__EXPERIMENTS__ && { experiments: window.__EXPERIMENTS__ }),
});
sessionStorage.setItem('loginPageUrl', window.location.href);
const { preset } = detectPreset(experiments);
const loaderColors = preset?.loaderColors?.fullPage;
const createAccountSettingsApi = () => new AccountSettingsHttpApi();
const createDealerApi = () => new DealerApi();
const i18n = window.__STATICS_URL__
  ? initI18n({
      locale: window.__LOCALE__,
      asyncMessagesLoader: (locale) =>
        import(`./assets/locales/messages_${locale}.json`).then((module) => {
          const translations = module.default ?? module;
          try {
            return abTranslate(window.__EXPERIMENTS__, translations);
          } catch (e) {
            return translations;
          }
        }),
      wait: true,
    })
  : initI18n({
      locale: window.__LOCALE__,
      messages: window.__MESSAGES__,
    });

// Conduct dedicated-scope experiments that affect first paint here, so the
// user never sees a control→variant flicker (default `users` scope is already
// resolved server-side via window.__EXPERIMENTS__). Wrap the conduction in
// `createSuspensePromise` and `.read()` it inside the <Suspense> boundary,
// passing the value into <AppLoader>. Example (retired SignupForFreeTitleAB):
//
//   const exp = createSuspensePromise(
//     conductExperiment({ key, scope, cidx: getCookie('_wixCIDX'), language: window.__LOCALE__ }),
//     { defaultValue: false, timeoutMs: 3000 },
//   );
//   // <AppLoader myExpEnabled={exp.read()} ... />

const FullPageLoader = (
  <Box
    height="50vh"
    verticalAlign="bottom"
    direction="vertical"
    align="center"
    style={
      loaderColors
        ? ({
            '--wds-loader-fill': loaderColors.fill,
            '--wds-loader-fill-loading': loaderColors.fillLoading,
          } as React.CSSProperties)
        : undefined
    }
  >
    <Loader />
  </Box>
);

const App = () => (
  <I18nextProvider i18n={i18n}>
    <ExperimentsProvider experiments={experiments}>
      <AppLoader
        createAccountSettingsApi={createAccountSettingsApi}
        createDealerApi={createDealerApi}
        i18n={i18n}
        panorama={panorama}
      />
    </ExperimentsProvider>
  </I18nextProvider>
);

ReactDOM.render(
  <Suspense fallback={FullPageLoader}>
    <App />
  </Suspense>,
  document.getElementById('root'),
);
